import type { DataSourceSummary, PipelineCounters, PipelineState, SourceEntity, SourceTable } from '../types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const pad = (n: number) => String(n).padStart(2, '0');
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "just now", "2 min ago", "3 h ago", "4 days ago", else "12 Sep". */
export function relativeTime(value: string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  const seconds = Math.round((Date.now() - d.getTime()) / 1000);
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDay(d);
}

/** 6715 → "6,715". */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US');
}

/** "12 Sep", with the year when it isn't this year. */
export function formatDay(value: string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  const year = d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${year}`;
}

/** "Today 10:42", "Yesterday 10:42", "12 Sep 10:42". */
export function formatDateTime(value: string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return '—';
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const now = new Date();
  if (sameDay(d, now)) return `Today ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return `Yesterday ${time}`;
  return `${formatDay(d)} ${time}`;
}

/** "10–15 Sep", "28 Aug – 3 Sep", "12 Sep". */
export function formatDayRange(first: string | Date | null | undefined, last: string | Date | null | undefined): string {
  const a = toDate(first);
  const b = toDate(last);
  if (!a && !b) return '—';
  if (!a || !b) return formatDay(a || b);
  if (sameDay(a, b)) return formatDay(a);
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
    return `${a.getDate()}–${formatDay(b)}`;
  }
  return `${formatDay(a)} – ${formatDay(b)}`;
}

/** Names for the tables in Aljex's own document. Any other table is shown by its own name. */
const KNOWN_TABLES: Record<string, { label: string; one: string; many: string }> = {
  loads: { label: 'Loads', one: 'load', many: 'loads' },
  carriers: { label: 'Carriers', one: 'carrier', many: 'carriers' },
  customers: { label: 'Customers', one: 'customer', many: 'customers' },
  employees: { label: 'Employees', one: 'employee', many: 'employees' },
  payments: { label: 'Payments', one: 'payment', many: 'payments' },
  invoicing: { label: 'Invoicing', one: 'invoice', many: 'invoices' },
  payrec: { label: 'Payments received', one: 'payment received', many: 'payments received' },
  debits: { label: 'Debits', one: 'debit', many: 'debits' },
  credits: { label: 'Credits', one: 'credit', many: 'credits' },
  salesreps: { label: 'Sales reps', one: 'sales rep', many: 'sales reps' },
  quotes: { label: 'Quotes', one: 'quote', many: 'quotes' },
  payable: { label: 'Payable', one: 'payable', many: 'payables' },
  new_purchases: { label: 'New purchases', one: 'purchase', many: 'purchases' }
};

/** "new_purchases" → "New purchases"; a table we have no name for keeps its own, tidied. */
export function entityLabel(entity: SourceEntity | null | undefined): string {
  if (!entity) return 'Records';
  const known = KNOWN_TABLES[entity];
  if (known) return known.label;
  const words = entity.replace(/[_.-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : entity;
}

/** [singular, plural] noun for one record of a table: "load" / "loads". */
export function entityNouns(entity: SourceEntity | null | undefined): [string, string] {
  const known = entity ? KNOWN_TABLES[entity] : undefined;
  return known ? [known.one, known.many] : ['record', 'records'];
}

/** Singular noun for one record of an entity: "load", "carrier" … */
export function entityNoun(entity: SourceEntity | null | undefined): string {
  return entityNouns(entity)[0];
}

/**
 * Every table a source has sent, in display order. An API that predates `tables`
 * only reports counts, so fall back to those.
 */
export function sourceTables(source: DataSourceSummary | null | undefined): SourceTable[] {
  if (!source) return [];
  if (Array.isArray(source.tables)) return source.tables;
  return Object.entries(source.counts ?? {}).map(([name, count]) => ({
    name,
    count,
    lastRecordAt: null,
    keyedBy: ['id'],
    documented: true
  }));
}

export const STATE_LABELS: Record<PipelineState, string> = {
  DRAFT: 'Draft',
  SHADOW: 'Shadow',
  LIVE: 'Live',
  PAUSED: 'Paused',
  NEEDS_ATTENTION: 'Needs attention'
};

/** "14 matched · 1 would create" — only the non-zero counters that matter at a glance. */
export function summariseCounters(c: PipelineCounters | null | undefined): string {
  if (!c) return '';
  const parts: string[] = [];
  if (c.matched) parts.push(`${formatCount(c.matched)} matched`);
  if (c.created) parts.push(`${formatCount(c.created)} created`);
  if (c.wouldCreate) parts.push(`${formatCount(c.wouldCreate)} would create`);
  if (c.adopted) parts.push(`${formatCount(c.adopted)} adopted`);
  if (c.updated) parts.push(`${formatCount(c.updated)} updated`);
  if (c.flagged) parts.push(`${formatCount(c.flagged)} flagged`);
  if (c.failed) parts.push(`${formatCount(c.failed)} failed`);
  return parts.join(' · ');
}

/** Message from a rejected request or a { success:false, message } envelope. */
export function errorText(error: any, fallback = 'Something went wrong'): string {
  if (typeof error === 'string' && error) return error;
  const message = error?.message;
  if (Array.isArray(message)) return message.join(', ');
  return typeof message === 'string' && message ? message : fallback;
}
